// "Which projects can I actually operate?" — asked by the console layout, the
// console home, and the project console. One definition, one place.
//
// THE JWT IS THE INDEX. Operator federation puts you in `{slug}:operator-owner`
// / `-admin` for exactly the projects you can administer, so the answer is
// already in the token before any I/O: this hook does N point GETs for the N
// projects you operate, and nothing that grows with the size of the platform.
//
// It used to be `Projects.where(pred).list()`, which reads as a query but is a
// CLIENT-SIDE filter over a FULL PREFIX SCAN — one sequential GetObject per
// manifest ON THE WHOLE PLATFORM, with the predicate applied to bytes already
// downloaded. That cost O(every project that exists) per call, and all three
// callers above run it, so opening ONE project's console walked the entire
// registry. At the ~1.3–2k projects an AWS account can hold (the IAM role
// ceiling) that is a minute of serial round-trips: the app list broke before
// the tenancy ceiling it was sitting under.
//
// NOT "manifests I own, UNION manifests I hold an operator group for": the
// `ownerSub === me.sub` half is gone, and dropping it is a CORRECTNESS fix as
// much as a speed one. A project you own but hold no `{slug}:operator-*` group
// for is one the project's Identity Pool refuses credentials for
// (AmbiguousRoleResolution "Deny"), so that half only ever contributed cards
// that opened to AccessDenied. It is reachable — the provision handler's
// operator seed is best-effort (it logs a warning and continues) — and such a
// project is now invisible here rather than visibly broken. Re-seed it with
// `s3p-admin provision-project --slug X --operator-email you@…` (idempotent);
// it cannot be repaired from the console, because repairing it would mean
// opening it.

import { operatorSlugsFromGroups } from "/sdk/index.js";
import { useAsync } from "./useAsync.ts";
import type { ProjectManifest } from "../lib/contracts.ts";
import { useSession } from "../session/SessionProvider.tsx";

/** How many manifest GETs are in flight at once. Independent, latency-bound,
 *  one round trip each — but bounded rather than unbounded, so an identity
 *  that operates hundreds of projects opens a working set of sockets instead
 *  of a few hundred at once. Mirrors UPLOAD_CONCURRENCY in the CLI's deploy. */
const LOOKUP_CONCURRENCY = 12;

export function useOperableProjects() {
	const { client, me } = useSession();

	return useAsync<ProjectManifest[]>(async () => {
		if (!client || !me) return [];
		// The operator-group grammar lives in the SDK (one definition):
		// `{slug}:operator-owner|-admin` for children, `{slug}:owner|admin`
		// for the platform pool itself. `app.operators.myProjects()` is the
		// same call for consumers that hold a client.
		const slugs = operatorSlugsFromGroups(me.groups ?? []);
		if (slugs.length === 0) return [];

		const Projects = client.collection<ProjectManifest>("projects", {
			filename: "manifest.json",
		});

		const found: ProjectManifest[] = [];
		const failures: unknown[] = [];
		let next = 0;
		await Promise.all(
			Array.from({ length: Math.min(LOOKUP_CONCURRENCY, slugs.length) }, async () => {
				while (next < slugs.length) {
					const slug = slugs[next++] as string;
					try {
						// null = a group naming a project with no manifest, i.e. a
						// group left behind by a teardown. Benign, and skipping it is
						// the whole repair: nothing else references it.
						const m = await Projects.get(slug);
						if (m) found.push(m);
					} catch (e) {
						failures.push(e);
					}
				}
			}),
		);
		// One unreadable manifest shouldn't hide the nine that loaded — but a
		// run where EVERYTHING failed is an error, not an empty account. Denied
		// and empty must never render identically.
		if (found.length === 0 && failures.length > 0) throw failures[0];

		// Stable order: newest first, so a just-created app is at the top.
		return found.sort((a, b) => (b.createdAt ?? "").localeCompare(a.createdAt ?? ""));
	}, [client, me?.sub, (me?.groups ?? []).join(",")]);
}
